import React, { useEffect, useRef } from 'react';

/**
 * Fixed-position soft radial gradient that follows the cursor.
 * Rendered pointer-events:none so it never blocks clicks. Automatically
 * disabled on touch devices and when prefers-reduced-motion is set.
 * Uses transform for GPU-cheap movement (no re-renders).
 */
const CursorSpotlight = ({
  size = 520,
  color = 'rgba(59,130,246,0.14)',
  edgeColor = 'rgba(16,185,129,0.06)',
}) => {
  const ref = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (window.matchMedia('(hover: none)').matches) return; // skip touch

    let rafId = 0;
    let tx = window.innerWidth / 2;
    let ty = window.innerHeight / 2;
    let x = tx;
    let y = ty;

    const el = ref.current;
    if (!el) return;
    el.style.opacity = '1';

    const onMove = (e) => {
      tx = e.clientX;
      ty = e.clientY;
    };
    const loop = () => {
      x += (tx - x) * 0.18;
      y += (ty - y) * 0.18;
      el.style.transform = `translate3d(${x - size / 2}px, ${y - size / 2}px, 0)`;
      rafId = requestAnimationFrame(loop);
    };

    window.addEventListener('pointermove', onMove, { passive: true });
    rafId = requestAnimationFrame(loop);
    return () => {
      window.removeEventListener('pointermove', onMove);
      cancelAnimationFrame(rafId);
    };
  }, [size]);

  return (
    <div
      ref={ref}
      aria-hidden="true"
      className="pointer-events-none fixed left-0 top-0 z-[1] mix-blend-multiply opacity-0 transition-opacity duration-500"
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: `radial-gradient(circle at center, ${color} 0%, ${edgeColor} 45%, transparent 70%)`,
        filter: 'blur(20px)',
        willChange: 'transform',
      }}
    />
  );
};

export default CursorSpotlight;
